import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Markdown from "@/components/Markdown";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const endRef = useRef(null);

  useEffect(() => {
    if (open && user && user !== false && msgs.length === 0) {
      api.get("/chat/history").then((r) => setMsgs(r.data)).catch(() => {});
    }
  }, [open, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const isAuthed = user && user !== false;

  const send = async () => {
    if (!input.trim() || busy) return;
    if (!isAuthed) return;
    const text = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text, id: Math.random() }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, id: Math.random() }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "🙏 Something went wrong. Try again.", id: Math.random() }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-toggle-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-56 right-6 lg:bottom-6 z-40 w-14 h-14 rounded-full bg-[#D4AF37] text-black shadow-2xl flex items-center justify-center hover:bg-[#E5C865] transition-colors"
          aria-label="Open Guru-ji chat"
        >
          <MessageCircle size={22} strokeWidth={1.5}/>
        </button>
      )}
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-20 right-6 lg:bottom-6 z-40 w-[min(400px,calc(100vw-32px))] h-[600px] max-h-[70vh] lg:max-h-[80vh] rounded-2xl bg-[#0A0C10] border border-white/10 shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/60">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
                <Sparkles size={16} className="text-[#D4AF37]"/>
              </div>
              <div>
                <div className="font-serif text-base leading-none">Guru-ji</div>
                <div className="text-[10px] text-[#A0A4AB] mt-1">AI Spiritual Assistant</div>
              </div>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-[#A0A4AB] hover:text-white"><X size={18}/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!isAuthed ? (
              <div className="text-center py-12 text-[#A0A4AB]">
                <div className="font-deva text-4xl text-[#D4AF37] mb-3">ॐ</div>
                Please sign in to chat with Guru-ji.
              </div>
            ) : msgs.length === 0 ? (
              <div className="text-center py-8">
                <div className="font-deva text-4xl text-[#D4AF37] mb-3">ॐ</div>
                <div className="text-[#A0A4AB] text-sm max-w-xs mx-auto">
                  Namaste! I am Guru-ji. Ask me about astrology, remedies, mantras, or life guidance.
                </div>
                <div className="mt-6 space-y-2">
                  {["What does my rising sign mean?", "A mantra for anxiety?", "Best day for griha-pravesh?"].map((q) => (
                    <button key={q} onClick={() => setInput(q)} className="block w-full text-xs text-left px-3 py-2 rounded-lg border border-white/10 text-[#A0A4AB] hover:border-[#D4AF37]/40 hover:text-[#EAE6DF] transition">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              msgs.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-[#D4AF37] text-black rounded-br-sm"
                      : "bg-[#12151C] border border-white/10 text-[#EAE6DF] rounded-bl-sm"
                  }`}>
                    {m.role === "assistant" ? <Markdown text={m.content}/> : m.content}
                  </div>
                </div>
              ))
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl bg-[#12151C] border border-white/10 text-sm text-[#A0A4AB]">
                  Guru-ji is contemplating<span className="inline-flex ml-1">
                    <span className="animate-pulse">·</span><span className="animate-pulse delay-100">·</span><span className="animate-pulse delay-200">·</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef}/>
          </div>

          {isAuthed && (
            <div className="p-3 border-t border-white/10 bg-black/60">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
                  placeholder="Ask Guru-ji…"
                  className="flex-1 resize-none px-3 py-2 rounded-lg bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none text-sm text-[#EAE6DF] max-h-32"
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="w-10 h-10 rounded-lg bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#E5C865] transition disabled:opacity-40"
                >
                  <Send size={16}/>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
